"use client"

import { useState } from "react"

import * as DialogPrimitive from "@radix-ui/react-dialog"
import { SearchIcon, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput
} from "@/components/ui/input-group"
import {
  Sheet,
  SheetDescription,
  SheetOverlay,
  SheetPortal,
  SheetTitle
} from "@/components/ui/sheet"
import { cn } from "@/lib/utils"
import { FilterIcon } from "@/src/shared/assets/icons"
import { useNavbarSearchStore } from "@/stores/navbar-search"

import { searchTexts } from "../../localization/texts"
import { GameResults } from "./GameResults"

// The bottom nav is z-120. A modal sheet would mark it inert, so Buscar could
// not toggle the panel closed. The sheet stops above the bar instead.
const PANEL_BOUNDS = "inset-x-0 top-18 bottom-[4.75rem] ls:hidden"

function SearchFields() {
  const setOpen = useNavbarSearchStore((state) => state.setOpen)
  const [query, setQuery] = useState("")

  return (
    <>
      <SheetTitle className="sr-only">{searchTexts.title}</SheetTitle>
      <SheetDescription className="sr-only">
        {searchTexts.placeholder}
      </SheetDescription>
      <div className="flex items-center gap-2">
        <InputGroup
          variant="surface"
          className="min-w-0 flex-1 focus-within:border-white/10"
        >
          <InputGroupAddon align="inline-start" className="pl-3 text-white">
            <SearchIcon className="size-6 shrink-0 opacity-50" aria-hidden />
          </InputGroupAddon>
          <InputGroupInput
            className="pl-2"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
            }}
            placeholder={searchTexts.placeholder}
            aria-label={searchTexts.placeholder}
          />
          <InputGroupAddon align="inline-end">
            <button
              type="button"
              onClick={() => {
                setOpen(false)
              }}
              aria-label={searchTexts.dismiss}
              className="text-white/50"
            >
              <X className="size-5" aria-hidden />
            </button>
          </InputGroupAddon>
        </InputGroup>
        <Button
          type="button"
          variant="muted"
          size="icon"
          className="size-11 shrink-0"
          aria-label={searchTexts.filters}
        >
          <FilterIcon className="size-5" aria-hidden />
        </Button>
      </div>
      <section aria-label={searchTexts.games}>
        <GameResults query={query} showRecommended />
      </section>
    </>
  )
}

export function SearchPanel() {
  const open = useNavbarSearchStore((state) => state.open)
  const setOpen = useNavbarSearchStore((state) => state.setOpen)

  return (
    <Sheet
      open={open}
      modal={false}
      onOpenChange={(next) => {
        setOpen(next)
      }}
    >
      <SheetPortal>
        <SheetOverlay
          className={PANEL_BOUNDS}
          onClick={() => {
            setOpen(false)
          }}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed z-50 flex flex-col gap-4 overflow-y-auto border-b border-black-600 bg-black-800 p-4 text-white shadow-lg outline-hidden",
            PANEL_BOUNDS
          )}
          onInteractOutside={(event) => {
            if (
              event.target instanceof Element &&
              event.target.closest("[data-betby-bottom-nav]")
            ) {
              event.preventDefault()
            }
          }}
          onFocusOutside={(event) => {
            if (
              event.target instanceof Element &&
              event.target.closest("[data-betby-bottom-nav]")
            ) {
              event.preventDefault()
            }
          }}
        >
          {open ? <SearchFields /> : null}
        </DialogPrimitive.Content>
      </SheetPortal>
    </Sheet>
  )
}
